import React, { useState } from 'react';
import { Modal, Button, Spinner } from 'react-bootstrap';

/**
 * ConfirmModal — the app's in-app replacement for window.confirm.
 *
 * Browser confirm boxes are unstyled, unbrandable, block the whole tab, and
 * force a yes/no on a single sentence — so consequences ("does this delete my
 * bookkeeping?") can't be explained. This renders the same decision in-app:
 * a title, the consequence in prose, optional detail of exactly WHAT is
 * affected, and labelled actions.
 *
 * Usage — keep the pending item in state and let `onConfirm` do the work:
 *
 *   const [target, setTarget] = useState(null);
 *   <ConfirmModal
 *     show={Boolean(target)}
 *     title="Ta bort rutinen?"
 *     body="Agenterna slutar följa den direkt."
 *     detail={target?.title}
 *     confirmLabel="Ta bort"
 *     onConfirm={async () => { await remove(target); setTarget(null); }}
 *     onCancel={() => setTarget(null)}
 *   />
 *
 * `onConfirm` may be async: the confirm button shows a spinner and the modal
 * refuses to close until it settles. A rejection leaves the modal open so the
 * caller can surface the error.
 */
const ConfirmModal = ({
    show,
    title = 'Är du säker?',
    body,
    detail,
    confirmLabel = 'Bekräfta',
    cancelLabel = 'Avbryt',
    variant = 'danger',
    onConfirm,
    onCancel,
}) => {
    const [busy, setBusy] = useState(false);

    const handleConfirm = async () => {
        try {
            setBusy(true);
            await onConfirm();
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal show={show} onHide={() => !busy && onCancel()} centered>
            <Modal.Header closeButton={!busy}>
                <Modal.Title style={{ fontSize: 17 }}>{title}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {body && <p style={{ fontSize: 14, marginBottom: detail ? 12 : 0 }}>{body}</p>}
                {detail && (
                    <div style={{ fontSize: 13, color: '#6b7280', background: '#f9fafb', borderRadius: 8, padding: '10px 12px' }}>
                        {detail}
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer style={{ borderTop: '1px solid #f3f4f6' }}>
                <Button variant="outline-secondary" size="sm" onClick={onCancel} disabled={busy}>
                    {cancelLabel}
                </Button>
                <Button variant={variant} size="sm" onClick={handleConfirm} disabled={busy}>
                    {busy ? <Spinner animation="border" size="sm" /> : confirmLabel}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ConfirmModal;
